<template>
  <div>

    <div class="card p-2" style="min-height: 200px">
      <div class="card-header-label">
        <div class="d-flex justify-content-between">
          <h3>Stage Update Log</h3>
          <div @click="openJobStageModal()">
            <h5 role="button" class="btn btn-primary">
              {{ currentJobStage ? currentJobStage.job_stage_name : 'Update Stage' }}
            </h5>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table">
          <thead>
          <tr>
            <th>#</th>
            <th>Date & Time</th>
            <th>Previous Stage</th>
            <th>Updated Stage</th>
            <th>Updated By</th>
            <th>Notes</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, i) in activityLogs" :key="i">
            <td>{{ ++i }}</td>
            <td>{{ item.start_time }}</td>
            <td>{{ item.previous_stage_name }}</td>
            <td>{{ item.beneficiary_stage_name }}</td>
            <td>{{ item.updated_by }}</td>
            <td v-html="item.full_description"></td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card p-2">
      <div class="card-header-label">
          <h3>Activity Target vs Achievement</h3>
      </div>
      <table class="table table-bordered">
        <thead>
        <tr>
          <th class="col-group">Group</th>
          <th class="col-activity-name">Activity Task Name</th>
          <th class="col-measurement">Measurement Type</th>
          <th class="col-narrow total-color">Total Target</th>
          <th class="col-narrow total-color">Already Achieved</th>
          <th class="col-narrow total-color">Balance</th>
          <th class="col-narrow current-color">Current Target (Period)</th>
          <th class="col-narrow current-color">Current Achievement</th>
          <th class="col-note current-color text-center">Note</th>
        </tr>
        </thead>
        <tbody>
        <TableTreeBrowser :node="project.project_accounts"/>
        </tbody>
      </table>
    </div>

    <div class="card p-2">
      <div class="card-header-label mt-2">
        <h3>General Task</h3>
      </div>
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
          <tr>
            <th>Activity Type</th>
            <th>Target (Hours)</th>
            <th>Actual (Hours)</th>
            <th>%</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-header-label">
        <div class="d-flex justify-content-between">
        <h3>Job Completion Details</h3>
        <div @click="statusUpdate(project)">
          <h5 role="button" class="btn btn-primary">
             Status Update
          </h5>
        </div>
        </div>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-12">
            <div class="info-block">
              <h5><span class="info-label">Completion Date:</span> {{ project.completed_date_time }}</h5>
            </div>
          </div>
          <div class="col-12">
            <div class="info-block">
              <h5><span class="info-label">Completed By:</span>{{ project.completed_user_name }}</h5>
            </div>
          </div>
          <div class="col-12">
            <div class="info-block">
              <h5><span class="info-label">Approved By:</span></h5>
            </div>
          </div>
          <div class="col-12">
            <div class="info-block">
              <h5>
                <span class="info-label">Project Delivery Timeline:</span>
                <span class="text-capitalize">{{ project.project_delivery_timeline }}</span>
              </h5>
            </div>
          </div>
          <div class="col-12">
            <div class="info-block">
              <h5>
                <span class="info-label">Client Satisfaction:</span>
                <span class="text-capitalize">{{ project.client_satisfaction }}</span>
              </h5>
            </div>
          </div>
          <div class="col-12">
            <div class="info-block">
              <h5>
                <span class="info-label">Team Excellence:</span>
                <span class="text-capitalize">{{ project.team_excellence }}</span>
              </h5>
            </div>
          </div>
          <div class="col-12">
            <div class="info-block">
              <h5><span class="info-label">Comments:</span>{{ project.comments }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-2"></div>
    <JobStageModal
        v-if="isJobStageModalOpen"
        :projectId="projectId"
        @getActivityLogData="fetchActivityLogsData"
        @close="closeJobStageModal"
    />
    <StatusUpdate
        v-if="statusUpdateModal"
        :selectedItem="selectedItem"
        @onClickClose="onClickClose"
        @getActivityLogData="fetchActivityLogsData"
    />
    <Loader v-if="loading"/>
  </div>

</template>

<script setup>

import {inject, onMounted, ref, watch} from "vue";
import Loader from "@/components/atom/LoaderComponent.vue";
import handleProjects from "@/services/modules/procurement/project";
import {useRoute, useRouter} from "vue-router";
import JobStageModal from "@/views/page/company/procurement/project/job-register/JobStageModal.vue";
import TableTreeBrowser from "@/views/page/company/contact/work-status/TableTreeBrowser.vue";
import StatusUpdate from '@/components/molecule/procurement/budget/report/project-tracker/StatusUpdate.vue'

const router = useRouter()
const route = useRoute()
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { fetchJobStageData } = handleProjects();
const loading = ref(false)
const isEdit = ref(false)
const activityLogs = ref([])
const project = ref({})
const currentJobStage = ref(null)
function getQuery() {
  return `?company_id=${route.params.companyId}`;
}

const isJobStageModalOpen = ref(false)
const projectId = ref(null)

const fetchActivityLogsData = () => {
  let query = getQuery();
  query += "&type=job_stage";
  query += "&project_id=" + route.query.projectId;
  activityLogs.value = [];
  currentJobStage.value = null;
  project.value = {};
  fetchJobStageData(query).then((res) => {
    if (!res.status) {
      return;
    }
    activityLogs.value = res.data.data;
    currentJobStage.value = res.data.job_stage;
    project.value = res.data.project;
  })
}

const openJobStageModal = () => {
  projectId.value = route.query.projectId
  isJobStageModalOpen.value = true
}

const closeJobStageModal = () => {
  isJobStageModalOpen.value = false
}

watch(() => route.query.projectId, (newValue) => {
  fetchActivityLogsData()
})
onMounted(() => {
  fetchActivityLogsData()
})

const statusUpdateModal = ref(false)
const selectedItem = ref({});
const statusUpdate = (item) => {
  selectedItem.value = item;
  statusUpdateModal.value = true
}
const updateItem = (itemId, status) => {
  emit('updateItem', itemId, status)
}
const onClickClose = () => {
  statusUpdateModal.value = false
}

</script>

<style scoped>
.card-header-label {
  background: #e8eaf6;
  color: #2c3e6b;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 10px 10px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid #d0d5e8;
}
.info-label {
  font-size: 14px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.05em;
  margin-bottom: 3px;
  line-height: 20px;
}
</style>
